import React, { useContext } from 'react';
import { Form, Typography, Divider, Radio } from 'antd';
import { getTranslatedString } from 'helpers/i18n';
import { ApplicationUIContext, Table } from '@yuhuinc/ui-library';
import { CurrentApplicationContext } from '../../ApplicationOverview';

const { Title } = Typography;

const translate = (key) =>
  getTranslatedString(`landlordPortal.applicationManagement.leaseOverview.${key}`);

const SERVICES = {
  gas: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.gas'),
  air_conditioning: getTranslatedString(
    'landlordPortal.servicesAndUtilitiesWizardPage.airConditioning'
  ),
  storage_space: getTranslatedString(
    'landlordPortal.servicesAndUtilitiesWizardPage.additionalStorageSpace'
  ),
  on_site_laundry: getTranslatedString(
    'landlordPortal.servicesAndUtilitiesWizardPage.onSiteLaundry'
  ),
  guest_parking: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.guestParking'),
};

const UTILITIES = {
  electricity: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.electricity'),
  heat: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.heat'),
  water: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.water'),
};

const IncludedRadio = ({ value, onChange, disabled }) => {
  return (
    <Radio
      value={true}
      checked={value === true}
      onChange={(event) => event.target.checked && onChange(true)}
      disabled={disabled}
    />
  );
};

const NotIncludedRadio = ({ value, onChange, disabled }) => {
  return (
    <Radio
      value={false}
      checked={value === false}
      onChange={(event) => event.target.checked && onChange(false)}
      disabled={disabled}
    />
  );
};

const LandlordRadio = ({ value, onChange, disabled }) => {
  return (
    <Radio
      value="landlord"
      checked={value === 'landlord'}
      onChange={(event) => event.target.checked && onChange('landlord')}
      disabled={disabled}
    />
  );
};

const ResidentRadio = ({ value, onChange, disabled }) => {
  return (
    <Radio
      value="resident"
      checked={value === 'resident'}
      onChange={(event) => event.target.checked && onChange('resident')}
      disabled={disabled}
    />
  );
};

const ServicesTable = ({ services, disabled }) => {
  const { other = [] } = services;
  const columns = [
    {
      title: translate('leaseDetails.service'),
      key: 'label',
      dataIndex: 'label',
      width: '72%',
    },
    {
      title: translate('leaseDetails.included'),
      key: 'included',
      align: 'center',
      render(_, record) {
        return (
          <Form.Item
            noStyle
            name={['services_and_utilities', 'services', record.key, 'is_included']}
          >
            <IncludedRadio disabled={disabled} />
          </Form.Item>
        );
      },
    },
    {
      title: translate('leaseDetails.notIncluded'),
      key: 'not_included',
      align: 'center',
      render(_, record) {
        return (
          <Form.Item
            noStyle
            name={['services_and_utilities', 'services', record.key, 'is_included']}
          >
            <NotIncludedRadio disabled={disabled} />
          </Form.Item>
        );
      },
    },
  ];

  const items = [
    ...Object.entries(services)
      .filter(([key, service]) => !!SERVICES[key] && service.is_included)
      .map(([key, service]) => ({ key, label: SERVICES[key], ...service })),
    ...other.map((service, index) => ({ label: service.name, key: index })),
  ];

  return (
    <Table
      size="small"
      hideActionAndSearch
      columns={columns}
      dataSource={items}
      pagination={false}
    />
  );
};

const UtilitiesTable = ({ utilities, disabled }) => {
  const columns = [
    {
      title: translate('leaseDetails.utility'),
      key: 'label',
      dataIndex: 'label',
      width: '72%',
    },
    {
      title: translate('leaseDetails.paidBylandlord'),
      key: 'landlord',
      align: 'center',
      render(_, record) {
        return (
          <Form.Item noStyle name={['services_and_utilities', 'utilities', record.key, 'paid_by']}>
            <LandlordRadio disabled={disabled} />
          </Form.Item>
        );
      },
    },
    {
      title: translate('leaseDetails.paidByresident'),
      key: 'resident',
      align: 'center',
      render(_, record) {
        return (
          <Form.Item noStyle name={['services_and_utilities', 'utilities', record.key, 'paid_by']}>
            <ResidentRadio disabled={disabled} />
          </Form.Item>
        );
      },
    },
  ];

  const items = Object.entries(utilities).map(([key, utility]) => ({
    key,
    label: UTILITIES[key],
    ...utility,
  }));

  return (
    <Table
      size="small"
      hideActionAndSearch
      columns={columns}
      dataSource={items}
      pagination={false}
    />
  );
};

const ServicesAndUtilities = ({ disabled }) => {
  const currentApplication = useContext(CurrentApplicationContext);
  const { isMobile } = useContext(ApplicationUIContext);

  return (
    <>
      <Title level={isMobile ? 4 : 5}>{translate('leaseDetails.services')}</Title>
      <ServicesTable
        services={currentApplication.property?.services_and_utilities?.services || {}}
        disabled
      />
      <Divider />
      <Title level={isMobile ? 4 : 5}>{translate('leaseDetails.utilities')}</Title>
      <UtilitiesTable
        utilities={currentApplication.property?.services_and_utilities?.utilities || {}}
        disabled
      />
    </>
  );
};

export default ServicesAndUtilities;
